import React, { useState } from 'react';
import {
  IAuditoriaDespesasAntecipadasResultado,
  IItemDespesaAntecipada,
  IItemQuebraCompetenciaDespesaAntecipada,
} from '../interfaces/IAuditoriaDespesasAntecipadas';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaDespesasAntecipadasResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-282: drill-down a nível de lançamento — todo ERRO precisa mostrar não só o saldo agregado,
// mas os lançamentos reais que compõem o saldo estagnado.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemDespesaAntecipada }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaDespesasAntecipadasDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const despesasEstagnadas = resultado.despesasEstagnadas || [];
  const despesasConciliadas = resultado.despesasConciliadas || [];
  const quebraCompetenciaMensal = resultado.quebraCompetenciaMensal || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Amortização de Despesas Antecipadas — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>34% IRPJ/CSLL + Multa ECD potencial (5% limitada a 1% da receita bruta do período — Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Despesas antecipadas sem amortização mensal no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à auditoria de amortização.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Amortização de Despesas Antecipadas:</strong> {resultado.fundamentacaoEstagnacao}</div>
          <div className='mt-2'><strong>Quebra de Competência — Fundamentação Fiscal:</strong> {resultado.fundamentacaoQuebraCompetenciaFiscal}</div>
          <div className='mt-2'><strong>Quebra de Competência — Fundamentação Contábil:</strong> {resultado.fundamentacaoQuebraCompetenciaContabil}</div>
        </div>
      )}

      {/* Épico "Apropriação Mensal - Competência" v1.1 — saldo que zera de uma vez, sem apropriação mensal gradual */}
      <div className='auditoria-card'>
        <h3>Quebra de Competência — Apropriação Mensal</h3>
        <p className='text-muted'>
          Meses em que uma despesa antecipada tinha saldo no mês anterior e zerou integralmente no mês corrente, sem
          indício de apropriação mensal gradual (1/N avos) — possível omissão de reconhecimento de despesa dedutível
          (Art. 25 da Lei nº 9.430/1996; Regime de Competência, CPC 00).
        </p>

        {quebraCompetenciaMensal.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma quebra de competência identificada no período.</div>
        )}

        {quebraCompetenciaMensal.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Competência</th>
                  <th className='text-end'>Valor Esperado (Base)</th>
                  <th className='text-end'>Impacto Fiscal (3%)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {quebraCompetenciaMensal.map((item: IItemQuebraCompetenciaDespesaAntecipada, idx) => {
                  const chave = `qc-${item.empresaId}-${item.contaContabil}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.competencia}</td>
                        <td className='text-end'>{formatCurrency(item.valorEsperado)}</td>
                        <td className='text-end'>{formatCurrency(item.impostoDevido)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Alerta:</strong> Quebra de Competência — Ausência de apropriação mensal de Despesa Antecipada.</div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico}.</div>
                              <div><strong>Conta Referencial:</strong> {item.contaReferencial || 'não mapeada'} - {item.descricaoConta}.</div>
                              <div><strong>Valor Esperado (Base):</strong> {formatCurrency(item.valorEsperado)}.</div>
                              <div><strong>Impacto Fiscal (3%):</strong> {formatCurrency(item.impostoDevido)}.</div>
                              <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoQuebraCompetenciaFiscal}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoQuebraCompetenciaContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Despesas Estagnadas (sem amortização mensal) */}
      <div className='auditoria-card'>
        <h3>Despesas Antecipadas sem Amortização Mensal</h3>
        <p className='text-muted'>
          Contas de Despesas Antecipadas (Ativo Circulante) com saldo devedor sem amortização (crédito) em algum mês
          do período — omissão que infla o Lucro Líquido e gera desembolso indevido de 34% de IRPJ/CSLL (CPC 27 / RIR 2018, Art. 311).
        </p>

        {despesasEstagnadas.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma despesa antecipada sem amortização identificada no período.</div>
        )}

        {despesasEstagnadas.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Última Amortização</th>
                  <th className='text-center'>Meses sem Amortização</th>
                  <th className='text-end'>Saldo Estagnado</th>
                  <th className='text-end'>Ônus Tributário (34%)</th>
                  <th className='text-end'>Impacto Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {despesasEstagnadas.map((item: IItemDespesaAntecipada, idx) => {
                  const chave = `de-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.dataUltimaAmortizacao ? formatDate(item.dataUltimaAmortizacao) : 'Nunca'}</td>
                        <td className='text-center'>
                          <span className={`auditoria-status status-${item.status}`}>{item.mesesSemAmortizacao} mês(es)</span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td className='text-end'>{formatCurrency(item.onusTributario)}</td>
                        <td className='text-end'>{formatCurrency(item.impactoFiscalTotal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>
                                Ônus tributário (34%): {formatCurrency(item.onusTributario)} |
                                Multa ECD Potencial: {formatCurrency(item.multaEcd)}
                              </div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Despesas Conciliadas — visibilidade de que a auditoria rodou */}
      {despesasConciliadas.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Despesas Antecipadas Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            Contas de despesas antecipadas que foram auditadas e estão em conformidade — amortização registrada em todos os meses ou saldo zerado.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Última Amortização</th>
                  <th className='text-end'>Saldo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {despesasConciliadas.map((item: IItemDespesaAntecipada, idx) => {
                  const chave = `dc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.dataUltimaAmortizacao ? formatDate(item.dataUltimaAmortizacao) : '-'}</td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaDespesasAntecipadasDetalhe;
